import * as React from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Input, Checkbox, NumericTextBox } from "@progress/kendo-react-inputs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Label } from "@progress/kendo-react-labels";
import { filterBy } from "@progress/kendo-data-query";
import { useActiveVats } from "services/vatService";
import { useNominalsBySubType } from "services/nominalsService";
import SubmitButton from "components/common/SubmitButton";

const CustomVatCode = ({ value, onChange, onVatChange }) => {
  const { data: activeVats = [] } = useActiveVats();
  const vatCodeId = typeof value === "object" ? value?.vatCodeId : value;
  const matched = activeVats.find((item) => item.vatCodeId === vatCodeId);
  const [valueVat, setValueVat] = React.useState(matched || null);
  const [vatData, setVatData] = React.useState(activeVats);

  React.useEffect(() => {
    const nextMatch = activeVats.find((item) => item.vatCodeId === vatCodeId);
    setValueVat(nextMatch || null);
  }, [vatCodeId, activeVats]);

  React.useEffect(() => {
    setVatData(activeVats.slice());
  }, [activeVats]);

  const handleChange = React.useCallback(
    (e) => {
      setValueVat(e.value);
      onChange?.({ value: e.value });
      onVatChange?.(e.value);
    },
    [onChange, onVatChange],
  );

  const handleFilterChange = (event) => {
    setVatData(filterBy(activeVats.slice(), event.filter));
  };

  return (
    <ComboBox
      data={vatData}
      value={valueVat}
      textField="display"
      dataItemKey="vatCodeId"
      filterable={true}
      onFilterChange={handleFilterChange}
      onChange={handleChange}
      placeholder="Search VAT Code"
    />
  );
};

const CustomNominalCode = ({ value, onChange }) => {
  const { data: expenseNominals = [] } = useNominalsBySubType(
    "ProfitAndLoss_Expense",
    { excludeArchived: true },
  );

  const matched = expenseNominals.find((item) => item.nominalRef === value);
  const [valueNominal, setValueNominal] = React.useState(matched || null);
  const [nominalData, setNominalData] = React.useState(expenseNominals);

  React.useEffect(() => {
    const nextMatch = expenseNominals.find((item) => item.nominalRef === value);
    setValueNominal(nextMatch || null);
  }, [value, expenseNominals]);

  React.useEffect(() => {
    setNominalData(expenseNominals.slice());
  }, [expenseNominals]);

  const handleChange = React.useCallback(
    (e) => {
      setValueNominal(e.value);
      onChange?.({ value: e.value?.nominalRef });
    },
    [onChange],
  );

  const handleFilterChange = (event) => {
    setNominalData(filterBy(expenseNominals.slice(), event.filter));
  };

  return (
    <ComboBox
      data={nominalData}
      value={valueNominal}
      textField="display"
      dataItemKey="nominalRef"
      filterable={true}
      onFilterChange={handleFilterChange}
      onChange={handleChange}
      placeholder="Search Nominal Ref"
    />
  );
};

const VatAmountField = ({ value, onChange }) => (
  <NumericTextBox
    format="n2"
    spinners={false}
    value={value ?? 0}
    onChange={(e) => onChange?.({ value: e.value })}
    className="right-align w-100"
  />
);

const NetAmountField = ({ value, onChange, onNetChange }) => (
  <NumericTextBox
    format="n2"
    spinners={false}
    value={value ?? 0}
    onChange={(e) => {
      onChange?.({ value: e.value });
      onNetChange?.(e.value);
    }}
    className="right-align w-100"
  />
);

const EditExpense = ({ cancelEdit, onSubmit, isLoading, item }) => {
  const IS_EDIT = Boolean(item?.expenseId);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const submitAttemptedRef = React.useRef(false);
  const { data: activeVats = [] } = useActiveVats();

  const validateForm = React.useCallback((values) => {
    if (!submitAttemptedRef.current) {
      return {};
    }

    const errors = {};
    if (!values.details || values.details.trim() === "") {
      errors.details = "Expense name is required";
    }
    if (
      values.vatCode === null ||
      values.vatCode === undefined ||
      values.vatCode === ""
    ) {
      errors.vatCode = "VAT Code is required";
    }
    return errors;
  }, []);

  const initialValues = React.useMemo(
    () => ({
      active: true,
      netAmount: 0,
      vatAmount: 0,
      ...item,
    }),
    [item]
  );

  return (
    <Dialog
      title={IS_EDIT ? "Edit Expense" : "Add Expense"}
      onClose={cancelEdit}
      width={700}
    >
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        validator={validateForm}
        render={(formRenderProps) => {
          const recalculateVat = (netAmount, vatCodeValue) => {
            const vatCodeId =
              typeof vatCodeValue === "object"
                ? vatCodeValue?.vatCodeId
                : vatCodeValue;
            const vat = activeVats.find((v) => v.vatCodeId === vatCodeId);
            if (vat && netAmount != null) {
              formRenderProps.onChange("vatAmount", {
                value: (vat.rate / 100) * netAmount,
              });
            }
          };

          return (
            <>
              <div className="p-3 ps-4">
                <FormElement style={{ maxWidth: 1200 }}>
                  <fieldset className="k-form-fieldset">
                    <div className="validation rounded px-3 py-3" style={{ boxShadow: "0px 0px 16px 0px rgba(0, 0, 0, 0.12)" }}>
                      <Label className="label mt-1 mb-3 w-100">Expense</Label>
                      <div className="row">
                        <div className="col-md-6">
                          <div
                            className={`mb-3 ${submitAttempted && formRenderProps.errors?.details
                              ? "validation-invalid"
                              : ""
                              }`}
                          >
                            <Label className="label mt-1 mb-2 w-100">Name</Label>
                            <Field
                              name="details"
                              component={Input}
                              placeholder="Expense name"
                            />
                          </div>

                          <div className="mb-3">
                            <Label className="label mt-1 mb-2 w-100">Nominal</Label>
                            <Field name="nominalRef" component={CustomNominalCode} />
                          </div>

                          <Field name="active" component={Checkbox} label="Active" />
                        </div>

                        <div className="col-md-6">
                          <div className="mb-3">
                            <Label className="label mt-1 mb-2 w-100">Net Amount</Label>
                            <Field
                              name="netAmount"
                              component={NetAmountField}
                              onNetChange={(netAmount) => {
                                recalculateVat(
                                  netAmount,
                                  formRenderProps.valueGetter("vatCode"),
                                );
                              }}
                            />
                          </div>

                          <div
                            className={`mb-3 ${submitAttempted && formRenderProps.errors?.vatCode
                              ? "validation-invalid"
                              : ""
                              }`}
                          >
                            <Label className="label mt-1 mb-2 w-100">VAT Code</Label>
                            <Field
                              name="vatCode"
                              component={CustomVatCode}
                              onVatChange={(vat) => {
                                recalculateVat(
                                  formRenderProps.valueGetter("netAmount"),
                                  vat,
                                );
                              }}
                            />
                          </div>

                          <div className="mb-3">
                            <Label className="label mt-1 mb-2 w-100">VAT Amount</Label>
                            <Field name="vatAmount" component={VatAmountField} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </fieldset>
                </FormElement>
              </div>
              <div className="dialog-footer">
                <button
                  className="bgBtnSecondary common-btn me-2"
                  onClick={cancelEdit}
                >
                  Cancel
                </button>
                <SubmitButton
                  className="me-3"
                  isLoading={isLoading}
                  onClick={() => {
                    submitAttemptedRef.current = true;
                    setSubmitAttempted(true);
                    formRenderProps.onSubmit();
                  }}
                  disabled={IS_EDIT ? !formRenderProps.modified : isLoading}
                >
                  {IS_EDIT ? "Update" : "Save"}
                </SubmitButton>
              </div>
            </>
          );
        }}
      />
    </Dialog>
  );
};

export default EditExpense;
